import React, {useMemo} from "react";
import {Autocomplete, ListItemText, ListSubheader} from "@mui/material";
import {groupBy, uniq} from "lodash";
import {Box, Checkbox, Chip, Colors, Container, FormControl, Text} from "@bounce/shared/ui";
import {IconChevronUp} from "@bounce/shared/ui/assets";
import {MenuItem, Select, TextField} from "../../../../ui/components";
import {autocompleteChevronSx} from "../../../../ui/components/inputStyles";
import {Option} from "../../../../apis/requests/segments/types";
import {joinFlagValue, splitFlagValue} from "../helpers";
import {BaseValueInputProps} from "./types";

type Props = BaseValueInputProps & {
  flagOptions: Option[];
  multi: boolean;
};

const groupOf = (o: Option): string => o.group ?? "";

const FlagValueInput: React.FC<Props> = ({value, valueError, onChange, flagOptions, multi, showLabel = true}) => {
  const flagNames = useMemo(() => uniq(flagOptions.map(groupOf)), [flagOptions]);
  const variantsByFlag = useMemo(() => groupBy(flagOptions, groupOf), [flagOptions]);

  if (multi) {
    const arr = Array.isArray(value) ? (value as string[]) : [];
    const allCombos = flagOptions.map((o) => joinFlagValue(groupOf(o), o.value));
    const menuChildren: React.ReactNode[] = [];
    flagNames.forEach((flag) => {
      menuChildren.push(
        <ListSubheader key={`__hdr_${flag}`} disableSticky>
          {flag}
        </ListSubheader>
      );
      (variantsByFlag[flag] ?? []).forEach((variant) => {
        const combo = joinFlagValue(flag, variant.value);
        menuChildren.push(
          <MenuItem key={combo} value={combo}>
            <Checkbox checked={arr.includes(combo)} />
            <ListItemText primary={variant.value} />
          </MenuItem>
        );
      });
    });
    return (
      <Box sx={{flex: 2}}>
        {showLabel && (
          <Text size={12} sx={{color: Colors.grey._700, mb: 0.5}}>
            Value
          </Text>
        )}
        <FormControl size="small" fullWidth error={!!valueError}>
          <Select
            multiple
            fullWidth
            displayEmpty
            value={arr.filter((v) => allCombos.includes(v))}
            onChange={(e) => {
              const raw = e.target.value;
              onChange(typeof raw === "string" ? [raw] : raw);
            }}
            renderValue={(selected) => (
              <Box sx={{display: "flex", flexWrap: "wrap", gap: 0.5}}>
                {(selected as string[]).map((v) => (
                  <Chip key={v} size_="small" variant_="blue" label={v} />
                ))}
              </Box>
            )}
          >
            {menuChildren}
          </Select>
        </FormControl>
      </Box>
    );
  }

  const {name: selectedName, variant: selectedVariant} =
    typeof value === "string" ? splitFlagValue(value) : {name: "", variant: ""};
  const variantsForSelected = variantsByFlag[selectedName] ?? [];

  return (
    <Box sx={{flex: 2, display: "flex", flexDirection: "column"}}>
      <Container row sx={{gap: 1}}>
        <Box sx={{flex: 3}}>
          {showLabel && (
            <Text size={12} sx={{color: Colors.grey._700, mb: 0.5}}>
              Flag
            </Text>
          )}
          <Autocomplete<string, false, false, false>
            size="small"
            popupIcon={<IconChevronUp style={{width: 20, height: 20}} />}
            sx={autocompleteChevronSx}
            options={flagNames}
            slotProps={{popper: {placement: "bottom-start", modifiers: [{name: "flip", enabled: false}]}}}
            value={selectedName || null}
            onChange={(_, nextName) => {
              if (!nextName) {
                onChange("");
                return;
              }
              const nextVariants = variantsByFlag[nextName] ?? [];
              const nextVariant = nextVariants.some((o) => o.value === selectedVariant)
                ? selectedVariant
                : nextVariants[0]?.value ?? "";
              onChange(joinFlagValue(nextName, nextVariant));
            }}
            renderInput={(params) => <TextField {...params} placeholder="Search flags…" error={!!valueError} />}
          />
        </Box>
        <Box sx={{flex: 2}}>
          {showLabel && (
            <Text size={12} sx={{color: Colors.grey._700, mb: 0.5}}>
              Variant
            </Text>
          )}
          <FormControl size="small" fullWidth disabled={!selectedName} error={!!valueError}>
            <Select
              fullWidth
              displayEmpty
              value={variantsForSelected.some((o) => o.value === selectedVariant) ? selectedVariant : ""}
              onChange={(e) => onChange(joinFlagValue(selectedName, e.target.value as string))}
            >
              {variantsForSelected.map((variant) => (
                <MenuItem key={variant.value} value={variant.value}>
                  {variant.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Container>
    </Box>
  );
};

export default FlagValueInput;
